import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import {
  EVChargerIssueType,
  useEVChargerStore,
} from '../../../../hooks/useEVChargerStore';
import { BinaryQuestionTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../../../../constants/errors';
import { ZodError } from 'zod';

export const SelectEVChargerIssue: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    update,
    resetSelective,
    data: { issueType, tellUsMoreDetails },
  } = useEVChargerStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'What is the issue?';

  const options = [
    {
      id: 'WiresExposed',
      name: 'WiresExposed',
      labelText: 'EV charging point wires exposed',
      isDefault: issueType === 'WiresExposed',
      isLabelBold: false,
    },
    {
      id: 'Damaged',
      name: 'Damaged',
      labelText: 'EV charging point has been damaged',
      isDefault: issueType === 'Damaged',
      isLabelBold: false,
    },
    {
      id: 'FailToStart',
      name: 'FailToStart',
      labelText: 'EV charging point session not starting',
      isDefault: issueType === 'FailToStart',
      isLabelBold: false,
    },
    {
      id: 'OutOfService',
      name: 'OutOfService',
      labelText: 'EV charging point showing out of service',
      isDefault: issueType === 'OutOfService',
      isLabelBold: false,
    },
    {
      id: 'AppError',
      name: 'AppError',
      labelText: 'Error message on App: charging point not activated',
      isDefault: issueType === 'AppError',
      isLabelBold: false,
    },
    {
      id: 'LabelMissing',
      name: 'LabelMissing',
      labelText: 'QR or EV charging point label missing',
      isDefault: issueType === 'LabelMissing',
      isLabelBold: false,
    },
    {
      id: 'SomethingElse',
      name: 'SomethingElse',
      labelText: `It's something else`,
      isDefault: issueType === 'SomethingElse',
      isLabelBold: false,
    },
  ];

  const handleChange = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as EVChargerIssueType;

    if (updatedSelection) {
      update('issueType', updatedSelection);
      update('tellUsMoreDetails', '');
    } else {
      resetSelective();
    }
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.MAP_LOCATION);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'issueType',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          issueType,
        });
        navigate(RoutesList.TELL_US_MORE);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            message: SELECT_OPTIONS_ERROR.required.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <BinaryQuestionTemplate
      title={title}
      options={options}
      onChange={handleChange}
      errorPath="issueType"
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
